import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Link } from '@ui/components/Link'
import { PageSection } from '@ui/components/PageSection'
import { Pagination, PaginationEventType } from '@ui/v2/Pagination/Pagination'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'
import { TradingAccountRestrictions, isRestricted } from '@utils/trading-account.utils'

import { AccountGroupType } from '@model/AccountGroupType'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { TradingAccount } from '@model/TradingAccount'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal as SharedInformationModal } from '@shared/modals/Information.modal'

import { TradingAccountBalanceCard } from './TradingAccountBalanceCard'
import { TradingAccountsTable } from './TradingAccountsTable'

export interface InitiateTransfer {
  tradingAccountId: string
  currency?: CurrencyType
  balance?: number
}

export interface InitiateConversion {
  currency?: CurrencyType
  balance?: number
  isTransferDisabled?: boolean
}

export interface TradingAccountOption {
  tradingAccount: TradingAccount
  currency: CurrencyType
}

export const TradingAccountBalances: React.FC = () => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const isMobile = useWindowResize()
  const apiClient = useApiClient(ClientApiClient)
  const { formatMoney } = useFormatNumber()

  const [TAOptionsData, setTAOptionsData] = useState<TradingAccountOption>()
  const balanceInfoModal = useModal(SharedInformationModal, {
    title: t('Trading Account.Trading Accounts Balance'),
    secondaryLabel: t('Got It'),
    description: () => (
      <Trans
        i18nKey='Wallet.Your current balance is the total of your {{ value }} balance(s)'
        components={{
          1: (
            <Link
              title={t('Currency you selected')}
              to={`/profile/account-settings`}
              size='lg'
              onClick={() => balanceInfoModal.close()}
            />
          ),
        }}
        values={{ value: 'trading account' }}
      />
    ),
  })
  const availableInfoModal = useModal(SharedInformationModal, {
    title: t('Wallet.Withdrawable Balance'),
    secondaryLabel: t('Got It'),
    description: () => (
      <Trans
        i18nKey='Wallet.Your withdrawable balance is the total amount you may withdraw, including your current open-position obligations'
        components={{
          1: (
            <Link
              title={t('Currency you selected')}
              to={`/profile/account-settings`}
              size='lg'
              onClick={() => availableInfoModal.close()}
            />
          ),
        }}
      />
    ),
  })

  const {
    data: tradingAccounts,
    isLoading,
    setPageQuery,
    meta: metaActiveTAs,
  } = usePaginatedQuery({
    queryKey: queryKeys.tradingAccount.balances(),
    queryFn: (pageQuery) =>
      apiClient.getTradingAccounts({
        ...pageQuery,
        search: {
          AccountGroupType: {
            value: AccountGroupType.Live,
            operator: Operator.EQUAL,
          },
          Platform: {
            value: PlatformTypeEnum.CQGCAST, // it's for ETD
            operator: Operator.EQUAL,
          },
          Status: {
            value: 1,
            operator: Operator.EQUAL,
          },
        },
      }),
  })

  const tradingAccount = tradingAccounts.length > 0 ? tradingAccounts[0] : null
  const hasTransferOutRestriction = isRestricted(
    TradingAccountRestrictions.TRANSFER_FROM,
    tradingAccount?.restrictions
  )

  const intiateTransfer = ({ tradingAccountId, currency, balance }: InitiateTransfer) => {
    if (Number(balance) >= 0) {
      navigate(`${CommonPaths.TradersRoomBalances}/${tradingAccountId}/transfer`, {
        state: {
          currency,
        },
      })
    } else {
      navigate(`${CommonPaths.TradersRoomWallets}/transfer`)
    }
    setTAOptionsData(undefined)
  }

  const initiateConversion = ({ currency, balance, isTransferDisabled }: InitiateConversion) => {
    if (isTransferDisabled) {
      addSnackbar.error({ message: t('This action is not available') })
      return
    }
    if (Number(balance) >= 0) {
      navigate(`/dashboard/traders-room/transactions/conversion`, {
        state: {
          tradingAccountId: TAOptionsData?.tradingAccount.id,
          currency,
        },
      })
    } else {
      navigate('/dashboard/traders-room/transactions/conversion')
    }
  }

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.TradingAccountBalancesHeader,
    isPaginationEntrySelected,
    isLoading,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(ScrollToIds.TradingAccountBalancesHeader, isLoading, metaActiveTAs?.pageSize)

  return (
    <div>
      {!isMobile && (
        <PageSection
          id={ScrollToIds.TradingAccountBalancesHeader}
          title={t('Tabs.Trading Accounts')}
          hasMinHeight
        />
      )}
      <TableSummary
        isLoading={isLoading}
        summaryItems={[
          {
            title: `${t('Trading Account.Trading Account')}, ${
              tradingAccount?.platformOverview.login || null
            }`,
            infoClick: () => balanceInfoModal.open(),
            value: (
              <>
                ≈
                {formatMoney(
                  tradingAccount?.platformOverview.balance,
                  tradingAccount?.platformOverview.reportingCurrency
                )}
              </>
            ),
          },
          {
            title: t('Wallet.Available to withdraw'),
            infoClick: () => availableInfoModal.open(),
            value: (
              <>
                ≈
                {formatMoney(
                  tradingAccount?.platformOverview.availableToWithdraw,
                  tradingAccount?.platformOverview.reportingCurrency
                )}
              </>
            ),
          },
        ]}
      />
      {isMobile ? (
        <div className='is-flex is-flex-direction-column'>
          {tradingAccount?.platformOverview?.balances?.map((balance, id) => (
            <TradingAccountBalanceCard
              key={id}
              hasTransferOutRestriction={hasTransferOutRestriction}
              initiateConversion={initiateConversion}
              TAOptionsData={TAOptionsData}
              setTAOptionsData={setTAOptionsData}
              initiateTransfer={() =>
                intiateTransfer({
                  tradingAccountId: tradingAccount.id,
                  currency: balance.currency,
                  balance: balance.balance,
                })
              }
              handleSetSelectedTradingAccount={() =>
                setTAOptionsData({
                  tradingAccount,
                  currency: balance.currency,
                })
              }
              balance={balance}
            />
          ))}
          {metaActiveTAs && (
            <Pagination
              scrollToHeaderId={ScrollToIds.TradingAccountBalancesHeader}
              pageData={metaActiveTAs}
              isLoading={isLoading}
              onPageChanged={(pageIndex, pageSize, eventType) => {
                if (eventType === PaginationEventType.ENTRIES_CHANGED) {
                  setIsPaginationEntrySelected(true)
                }
                setPageQuery!({
                  pageIndex,
                  pageSize,
                })
              }}
            />
          )}
        </div>
      ) : (
        <Loading showLoadingIcon isLoading={isLoading}>
          {tradingAccount && (
            <TradingAccountsTable
              initiateTransfer={(currency) =>
                intiateTransfer({
                  tradingAccountId: tradingAccount.id,
                  currency,
                  balance: tradingAccount.platformOverview.balances.find(
                    (b) => b.currency === currency
                  )?.balance,
                })
              }
              hasTransferOutRestriction={hasTransferOutRestriction}
              initiateConversion={initiateConversion}
              balances={tradingAccount?.platformOverview.balances}
              TAOptionsData={TAOptionsData}
              setTAOptionsData={setTAOptionsData}
              handleSetSelectedTradingAccount={(currency) =>
                setTAOptionsData({ tradingAccount, currency })
              }
            />
          )}
        </Loading>
      )}
    </div>
  )
}
